import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tabs,
  Typography,
  Upload,
} from "antd";
import { toast } from "react-toastify";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import getCompanyIdFromToken from "../../utils/getCompanyIdFromToken";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import Loader from "../loader/loader";

const client_Id = getCompanyIdFromToken();
const { TabPane } = Tabs;
const { Title } = Typography;
const { Option } = Select;

// Mock city-pincode mapping
const cityPincodeMap = {
  Mumbai: "400001",
  Delhi: "110001",
  Bangalore: "560001",
  Chennai: "600001",
};

const AddDetails = () => {
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [initValues, setInitValues] = useState([]);
  const [isAddingNewOffice, setIsAddingNewOffice] = useState(false);
  const [officeNames, setOfficeNames] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [thumbFileList, setThumbFileList] = useState([]);
  const [bankEntries, setBankEntries] = useState([{}]);
  const [documentEntries, setDocumentEntries] = useState([{}]);

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => {
    setIsModalVisible(false);
    modalForm.resetFields();
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get("setting/getCompanySetting");
      if (response?.data?.result?.length) {
        const officeNamesArray = response.data.result.map(
          (setting) => setting.company_name
        );
        setInitValues(response.data.result);
        setOfficeNames(officeNamesArray);
        const defaultOffice = officeNamesArray[0];
        const defaultOfficeDetails = response.data.result.find(
          (setting) => setting.company_name === defaultOffice
        );
        if (defaultOfficeDetails) {
          form.setFieldsValue({
            selectedOffice: defaultOffice,
            ...defaultOfficeDetails,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to fetch settings");
    }
  };

  const addNewOffice = async (values) => {
    try {
      const cleanedValues = {
        ...values,
        phone: values.phone ? `+91${values.phone}` : "",
        bankEntries,
        documentEntries,
      };
      const response = await axios.post("setting/addOffice", cleanedValues);
      if (response.status === 200 || response.status === 201) {
        await fetchSettings();
        return "success";
      }
      throw new Error("Failed to add office");
    } catch (error) {
      console.error("Error in addNewOffice:", error);
      throw error;
    }
  };

  const updateInvoice = async (values) => {
    try {
      const selectedcompanyId = initValues.find(
        (setting) => setting.company_name === values.selectedOffice
      )?.id;
      if (!selectedcompanyId) throw new Error("Selected office not found");
      const response = await axios.put(`setting/${selectedcompanyId}`, {
        ...values,
        bankEntries,
        documentEntries,
      });
      if (response.status === 200 || response.status === 201) {
        await fetchSettings();
        return "success";
      }
      throw new Error("Failed to update office");
    } catch (error) {
      console.error("Error in updateInvoice:", error);
      throw error;
    }
  };

  const onClickLoading = async (addNewOfficeFlag) => {
    setLoader(true);
    try {
      const formValues = form.getFieldsValue(true);
      const resp = await (addNewOfficeFlag
        ? addNewOffice(formValues)
        : updateInvoice(formValues));
      if (resp === "success") {
        toast.success(
          addNewOfficeFlag
            ? "New Office Added Successfully"
            : "Office Details Updated Successfully"
        );
        form.resetFields();
        setIsAddingNewOffice(false);
        setBankEntries([{}]);
        setDocumentEntries([{}]);
      }
    } catch (error) {
      toast.error(error.message || "Failed to add/update office");
    } finally {
      setLoader(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await modalForm.validateFields();
      const response = await axios.post("setting/addOfficeType", {
        officeType: values.officeType,
        company_id: client_Id,
        status: true,
      });
      if (response.data.success) {
        const typesResponse = await axios.get("setting/getOfficeTypes");
        if (typesResponse?.data?.length) setCompanyTypes(typesResponse.data);
        toast.success("Office Type added successfully!");
        setIsModalVisible(false);
        modalForm.resetFields();
      } else {
        toast.error("Failed to add Office Type");
      }
    } catch (error) {
      toast.error(error.message || "Error adding Office Type");
    }
  };

  const handleCityChange = (city) => {
    const pincode = cityPincodeMap[city] || "";
    form.setFieldsValue({ pincode });
  };

  const addBankEntry = () => setBankEntries([...bankEntries, {}]);
  const removeBankEntry = (index) => {
    const newEntries = bankEntries.filter((_, i) => i !== index);
    setBankEntries(newEntries);
  };

  const addDocumentEntry = () => setDocumentEntries([...documentEntries, {}]);
  const removeDocumentEntry = (index) => {
    const newEntries = documentEntries.filter((_, i) => i !== index);
    setDocumentEntries(newEntries);
  };

  return (
    <Fragment>
      <UserPrivateComponent permission={"create-setting"}>
        <Row className="mr-top justify-center">
          <Col xs={24} sm={24} md={20} lg={16} className="border rounded shadow-lg bg-white p-4">
            {/* <Card bordered={false}> */}
              <Title level={4} className="text-center text-xl font-bold mb-4">
                Company Settings
              </Title>
              {loader ? (
                <Loader />
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  name="company_settings"
                  onFinish={() => onClickLoading(isAddingNewOffice)}
                  className="space-y-2"
                >
                  <Tabs defaultActiveKey="1" className="bg-gray-50 p-2 rounded-lg">
                    <TabPane tab="Basic Info" key="1">
                      <Row gutter={[12, 12]} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Col className="md:col-span-1">
                          <Form.Item
                            label="Company Logo"
                            name="company_logo"
                            className="w-full"
                          >
                            <Upload
                              listType="picture-card"
                              beforeUpload={() => false}
                              fileList={thumbFileList}
                              maxCount={1}
                              onChange={({ fileList }) => setThumbFileList(fileList)}
                              accept="image/jpeg,image/png"
                              className="inline-flex items-center justify-center w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors"
                            >
                              {thumbFileList.length >= 1 ? null : (
                                <div className="text-center">
                                  <PlusOutlined className="text-xl" />
                                  <div className=" text-xs">Upload</div>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col className="md:col-span-2">
                          <Row gutter={[12, 12]} className="grid grid-cols-1 md:grid-cols-2 gap-0">
                            <Col>
                              <Form.Item
                                label="Company Name"
                                name="CompanyName"
                                rules={[{ required: true, message: "Please input Company Name!" }]}
                              >
                                <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Registered Address"
                                name="address"
                                rules={[{ required: true, message: "Please input address!" }]}
                              >
                                <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="City"
                                name="city"
                                rules={[{ required: true, message: "Please select city!" }]}
                              >
                                <Select
                                  onChange={handleCityChange}
                                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm"
                                >
                                  {Object.keys(cityPincodeMap).map((city) => (
                                    <Option key={city} value={city}>
                                      {city}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="State"
                                name="state"
                                rules={[{ required: true, message: "Please input state!" }]}
                              >
                                <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Pincode"
                                name="pincode"
                                rules={[{ required: true, message: "Please input pincode!" }]}
                              >
                                <Input disabled className="w-full rounded-lg border-gray-300 bg-gray-100 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Phone"
                                name="phone"
                                rules={[
                                  { required: true, message: "Please input phone number!" },
                                  { pattern: /^[0-9]{10}$/, message: "Please enter a valid 10-digit phone number!" },
                                ]}
                              >
                                <Input addonBefore="+91" maxLength={10} className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                  { required: true, message: "Please input email!" },
                                  { type: "email", message: "Please enter a valid email!" },
                                ]}
                              >
                                <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Website"
                                name="website"
                                rules={[{ type: "url", message: "Please enter a valid URL!" }]}
                              >
                                <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Password"
                                name="password"
                                rules={[{ required: true, message: "Please input password!" }]}
                              >
                                <Input.Password className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Form.Item
                                label="Confirm Password"
                                name="confirm_password"
                                dependencies={["password"]}
                                rules={[
                                  { required: true, message: "Please confirm password!" },
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      if (!value || getFieldValue("password") === value) {
                                        return Promise.resolve();
                                      }
                                      return Promise.reject(new Error("Passwords do not match!"));
                                    },
                                  }),
                                ]}
                              >
                                <Input.Password className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </TabPane>
                    <TabPane tab="Registration" key="2">
                      <Row gutter={[12, 12]} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Col>
                          <Form.Item label="GST Number" name="gst_number">
                            <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item label="TAN Number" name="tan_number">
                            <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item label="PF Number" name="pf_number">
                            <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </TabPane>
                    <TabPane tab="Bank Info" key="3">
                      {bankEntries.map((_, index) => (
                        <Row gutter={[12, 12]} key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                          <Col>
                            <Form.Item
                              label="Account Name"
                              name={["bankEntries", index, "accountName"]}
                              rules={[{ required: true, message: "Please input account name!" }]}
                            >
                              <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="Account Number"
                              name={["bankEntries", index, "accountNumber"]}
                              rules={[{ required: true, message: "Please input account number!" }]}
                            >
                              <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="Bank Name"
                              name={["bankEntries", index, "bankName"]}
                              rules={[{ required: true, message: "Please input bank name!" }]}
                            >
                              <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="Opening Balance"
                              name={["bankEntries", index, "openingBalance"]}
                              rules={[{ required: true, message: "Please input opening balance!" }]}
                            >
                              <Input type="number" className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="As on Date"
                              name={["bankEntries", index, "asOnDate"]}
                              rules={[{ required: true, message: "Please select date!" }]}
                            >
                              <DatePicker className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            {bankEntries.length > 1 && (
                              <Button
                                danger
                                onClick={() => removeBankEntry(index)}
                                className="mt-1 text-sm"
                              >
                                Remove
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={addBankEntry} block className="bg-blue-500 text-white hover:bg-blue-600 text-sm">
                        Add Bank Entry
                      </Button>
                    </TabPane>
                    <TabPane tab="Other" key="4">
                      <Row gutter={[12, 12]} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Col>
                          <Form.Item
                            label="Industry Type"
                            name="industry_type"
                            rules={[{ required: true, message: "Please select industry type!" }]}
                          >
                            <Select className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm">
                              <Option value="IT">IT</Option>
                              <Option value="Manufacturing">Manufacturing</Option>
                              <Option value="Retail">Retail</Option>
                              <Option value="Healthcare">Healthcare</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item
                            label="Company Size"
                            name="company_size"
                            rules={[{ required: true, message: "Please select company size!" }]}
                          >
                            <Select className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm">
                              <Option value="Small">Small</Option>
                              <Option value="Medium">Medium</Option>
                              <Option value="Large">Large</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    </TabPane>
                    <TabPane tab="Document Type" key="5">
                      {documentEntries.map((_, index) => (
                        <Row gutter={[12, 12]} key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                          <Col>
                            <Form.Item
                              label="Document Type"
                              name={["documentEntries", index, "documentType"]}
                              rules={[{ required: true, message: "Please input document type!" }]}
                            >
                              <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="Upload Document"
                              name={["documentEntries", index, "document"]}
                              rules={[{ required: true, message: "Please upload document!" }]}
                            >
                              <Upload beforeUpload={() => false} maxCount={1} className="w-full">
                                <Button icon={<PlusOutlined />} className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm">
                                  Upload
                                </Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                          <Col>
                            <Form.Item
                              label="Expiry Date"
                              name={["documentEntries", index, "expiryDate"]}
                              rules={[{ required: true, message: "Please select expiry date!" }]}
                            >
                              <DatePicker className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
                            </Form.Item>
                          </Col>
                          <Col>
                            {documentEntries.length > 1 && (
                              <Button
                                danger
                                onClick={() => removeDocumentEntry(index)}
                                className="mt-1 text-sm"
                              >
                                Remove
                              </Button>
                            )}
                          </Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={addDocumentEntry} block className="bg-blue-500 text-white hover:bg-blue-600 text-sm">
                        Add Document Entry
                      </Button>
                      <div className="flex justify-end space-x-2 mt-4">
                        <Button
                          shape="round"
                          size="middle"
                          onClick={() => setIsAddingNewOffice(true)}
                          htmlType="submit"
                          loading={loader && isAddingNewOffice}
                          className="bg-green-500 text-white hover:bg-green-600 text-sm px-4 py-1"
                        >
                          Add New Office
                        </Button>
                        <Button
                          type="primary"
                          shape="round"
                          size="middle"
                          onClick={() => setIsAddingNewOffice(false)}
                          htmlType="submit"
                          loading={loader && !isAddingNewOffice}
                          className="bg-blue-600 text-white hover:bg-blue-700 text-sm px-4 py-1"
                        >
                          Update Office Details
                        </Button>
                      </div>
                    </TabPane>
                  </Tabs>
                </Form>
              )}
            {/* </Card> */}
          </Col>
        </Row>
        <Modal
          title="Add Office Type"
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          className="rounded-lg"
        >
          <Form form={modalForm} layout="vertical">
            <Form.Item
              label="Office Type"
              name="officeType"
              rules={[{ required: true, message: "Please input office type!" }]}
            >
              <Input className="w-full rounded-lg border-gray-300 focus:border-blue-500 text-sm" />
            </Form.Item>
          </Form>
        </Modal>
      </UserPrivateComponent>
    </Fragment>
  );
};

export default AddDetails;

// import {
//   Button,
//   Card,
//   Col,
//   Form,
//   Input,
//   Modal,
//   Row,
//   Select,
//   Typography,
//   Upload,
// } from "antd";
// import { toast } from "react-toastify";
// import getSetting from "../../api/getSettings";
// import { PlusOutlined } from "@ant-design/icons";
// import axios from "axios";
// import { Fragment, useEffect, useState } from "react";
// import getCompanyIdFromToken from "../../utils/getCompanyIdFromToken";
// import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
// import Loader from "../loader/loader";
// import styles from "./AddDetails.module.css";

// const client_Id = getCompanyIdFromToken();

// const AddDetails = () => {
//   const { Title } = Typography;
//   const { Option } = Select;

//   const [loader, setLoader] = useState(false);
//   const [form] = Form.useForm();
//   const [modalForm] = Form.useForm();
//   const [initValues, setInitValues] = useState([]);
//   const [isAddingNewOffice, setIsAddingNewOffice] = useState(false);
//   const [officeNames, setOfficeNames] = useState([]);
//   const [companyTypes, setCompanyTypes] = useState([]);
//   const [isModalVisible, setIsModalVisible] = useState(false);
//   const [selectedOfficeType, setSelectedOfficeType] = useState(null);
//   const [thumbFileList, setThumbFileList] = useState([]);
//   const showModal = () => {
//     setIsModalVisible(true);
//   };

//   // Handle modal close
//   const handleCancel = () => {
//     setIsModalVisible(false);
//     modalForm.resetFields();
//   };

//   const fetchSettings = async () => {
//     try {
//       const response = await getSetting();
//       console.log("Fetched settings response:", response);
//       if (response?.result?.length) {
//         const officeNamesArray = response.result.map(
//           (setting) => setting.company_name
//         );
//         setInitValues(response.result);
//         setOfficeNames(officeNamesArray);

//         // Set the default office and its details
//         const defaultOffice = officeNamesArray[0];
//         const defaultOfficeDetails = response.result.find(
//           (setting) => setting.company_name === defaultOffice
//         );
//         console.log("Default office details:", defaultOfficeDetails);

//         if (defaultOfficeDetails) {
//           form.setFieldsValue({
//             selectedOffice: defaultOffice,
//             ...defaultOfficeDetails,
//             officeTypeId: defaultOfficeDetails.officeTypeId, // Ensure officeTypeId is set
//           });
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching settings:", error);
//       toast.error("Failed to fetch settings");
//     }
//   };

//   const addNewOffice = async (values) => {
//     try {
//       const cleanedValues = {
//         company_name: values.company_name || "",
//         tag_line: values.tag_line || null,
//         address: values.address || "",
//         phone: (values.phone || "")
//           .replace(/^\+91\+91/, "+91")
//           .replace(/^\+91/, ""),
//         email: values.email || "",
//         website: values.website || "",
//         footer: values.footer || "",
//         officeTypeId: values.officeTypeId || null,
//         gst: values.gst || null,
//       };

//       const response = await axios({
//         method: "post",
//         headers: {
//           Accept: "application/json",
//           "Content-Type": "application/json;charset=UTF-8",
//         },
//         url: `setting/addOffice`,
//         data: cleanedValues,
//       });

//       if (response.status === 200 || response.status === 201) {
//         await fetchSettings(); // Refresh settings after adding
//         return "success";
//       }
//       throw new Error("Failed to add office");
//     } catch (error) {
//       console.error("Error in addNewOffice:", error);
//       throw error;
//     }
//   };

//   const updateInvoice = async (values) => {
//     try {
//       if (!Array.isArray(initValues)) {
//         throw new Error("Initial values not properly loaded");
//       }

//       const selectedcompanyId = initValues.find(
//         (setting) => setting.company_name === values.selectedOffice
//       )?.id;

//       if (!selectedcompanyId) {
//         throw new Error("Selected office not found");
//       }

//       const response = await axios({
//         method: "put",
//         headers: {
//           Accept: "application/json",
//           "Content-Type": "application/json;charset=UTF-8",
//         },
//         url: `setting/${selectedcompanyId}`,
//         data: {
//           ...values,
//           officeTypeId: values.officeTypeId,
//         },
//       });

//       if (response.status === 200 || response.status === 201) {
//         await fetchSettings(); // Refresh settings after updating
//         return "success";
//       }
//       throw new Error("Failed to update office");
//     } catch (error) {
//       console.error("Error in updateInvoice:", error);
//       throw error;
//     }
//   };

//   const onClickLoading = async (values, addNewOfficeFlag) => {
//     setLoader(true);
//     try {
//       const formValues = form.getFieldsValue(true);
//       formValues.phone = formValues.phone ? `+91${formValues.phone}` : "";

//       const resp = await (addNewOfficeFlag
//         ? addNewOffice(formValues)
//         : updateInvoice(formValues));

//       if (resp === "success") {
//         toast.success(
//           addNewOfficeFlag
//             ? "New Office Added Successfully"
//             : "Office Details Updated Successfully"
//         );

//         //   if (addNewOfficeFlag) {
//         //     form.resetFields();
//         //     setIsAddingNewOffice(false);
//         //   }
//         // }
//         if (addNewOfficeFlag) {
//           form.resetFields(); // Reset all fields, including officeTypeId
//           setIsAddingNewOffice(false);
//         } else {
//           form.setFieldsValue({ officeTypeId: null }); // Clear officeTypeId after update
//         }

//         // Clear the selected office type
//         setSelectedOfficeType(null);
//       }
//     } catch (error) {
//       console.error("Error in onClickLoading:", error);
//       toast.error(error.message || "Failed to add/update office");
//     } finally {
//       setLoader(false);
//     }
//   };

//   // API call to post the new office type
//   const addNewOfficeType = async (values) => {
//     try {
//       const response = await axios.post("setting/addOfficeType", {
//         officeType: values.officeType,
//         company_id: client_Id,
//         status: true,
//       });

//       if (response.data.success) {
//         toast.success("Office Type added successfully!");
//         setIsModalVisible(false);
//       } else {
//         toast.error({ message: "Failed to add Office Type" });
//       }
//     } catch (error) {
//       // toast.error({ message: 'Error adding Office Type', description: error.message });
//     }
//   };

//   const handleOk = async () => {
//     try {
//       const values = await modalForm.validateFields();

//       // Log the values being sent
//       console.log("Sending office type values:", {
//         officeType: values.officeType,
//         company_id: client_Id,
//         status: true,
//       });

//       const response = await axios.post("setting/addOfficeType", {
//         officeType: values.officeType,
//         company_id: client_Id,
//         status: true,
//       });

//       console.log("Add Office Type Response:", response);

//       // Check response more comprehensively
//       if (
//         response.data &&
//         (response.data.success ||
//           response.status === 200 ||
//           response.status === 201)
//       ) {
//         // Fetch updated office types
//         const typesResponse = await axios.get("setting/getOfficeTypes");

//         console.log("Fetched Office Types:", typesResponse.data);

//         if (typesResponse?.data?.length) {
//           setCompanyTypes(typesResponse.data);
//         }

//         toast.success("Office Type added successfully!");
//         setIsModalVisible(false);
//         modalForm.resetFields(); // Clear the modal form
//       } else {
//         // More detailed error logging
//         console.error("Failed to add office type. Response:", response);
//         toast.error(response.data.message || "Failed to add Office Type");
//       }
//     } catch (error) {
//       // Comprehensive error handling
//       console.error("Full error when adding office type:", error);

//       // Check if it's an axios error with response
//       if (error.response) {
//         // The request was made and the server responded with a status code
//         // that falls out of the range of 2xx
//         console.error("Error response data:", error.response.data);
//         console.error("Error response status:", error.response.status);
//         console.error("Error response headers:", error.response.headers);

//         toast.error(
//           error.response.data.message ||
//             `Failed to add Office Type. Status: ${error.response.status}`
//         );
//       } else if (error.request) {
//         // The request was made but no response was received
//         console.error("No response received:", error.request);
//         toast.error("No response from server. Please check your connection.");
//       } else {
//         // Something happened in setting up the request that triggered an Error
//         console.error("Error setting up request:", error.message);
//         toast.error(`Error: ${error.message}`);
//       }
//     }
//   };

//   useEffect(() => {
//     setLoader(true);
//     axios
//       .get("setting/getOfficeTypes")
//       .then((response) => {
//         if (response?.data?.length) {
//           setCompanyTypes(response.data);
//         } else {
//           console.error(
//             "Invalid data received from getOfficeTypes API:",
//             response
//           );
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching office types:", error);
//       })
//       .finally(() => {
//         setLoader(false);
//       });

//     getSetting()
//       .then((response) => {
//         if (response?.result?.length) {
//           const officeNamesArray = response.result.map(
//             (setting) => setting.company_name
//           );
//           setInitValues(response.result);
//           setOfficeNames(officeNamesArray);

//           // Set the default office and its details
//           const defaultOffice = officeNamesArray[0];
//           const defaultOfficeDetails = response.result.find(
//             (setting) => setting.company_name === defaultOffice
//           );

//           if (defaultOfficeDetails) {
//             form.setFieldsValue({
//               selectedOffice: defaultOffice,
//               ...defaultOfficeDetails,
//             });
//           }
//         } else {
//           console.error("Invalid data received from getSetting API:", response);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching settings:", error);
//       })
//       .finally(() => {
//         setLoader(false);
//       });
//   }, [form]);

//   const onFinish = async (values) => {
//     try {
//       // Ensure officeTypeId is the correct ID
//       const selectedOfficeType = companyTypes.find(
//         (type) => type.officeType === values.officeTypeId
//       );

//       // Clean up values
//       const cleanedValues = {
//         company_name: values.company_name || "",
//         tag_line: values.tag_line || null,
//         address: values.address || "",
//         phone: (values.phone || "")
//           .replace(/^\+91\+91/, "+91")
//           .replace(/^\+91/, ""),
//         email: values.email || "",
//         website: values.website || "",
//         footer: values.footer || "",
//         officeTypeId: selectedOfficeType
//           ? selectedOfficeType.id
//           : values.officeTypeId,
//         gst: values.gst || null,
//       };

//       if (isAddingNewOffice) {
//         const resp = await addNewOffice(cleanedValues);
//         if (resp === "success") {
//           toast.success("New Office Added Successfully");
//           setInitValues({});
//           setIsAddingNewOffice(false);
//         }
//       } else {
//         const resp = await updateInvoice(cleanedValues, false, initValues);
//         if (resp === "success") {
//           toast.success("Office Details Updated Successfully");
//           setInitValues({});
//         }
//       }
//     } catch (error) {
//       console.error("Error updating office details:", error);
//     }
//   };

//   const onFinishFailed = (errorInfo) => {
//     toast.error("Something went wrong !");
//     console.log("Failed:", errorInfo);
//   };

//   const handleThumbImageChange = ({ fileList }) => {
//     console.log("fileList", fileList);
//     setThumbFileList(fileList);
//   };

//   const handleOfficeChange = (selectedOffice) => {
//     console.log("Selected office:", selectedOffice); // Debug log

//     if (selectedOffice === "addNew") {
//       setIsAddingNewOffice(true);
//       setSelectedOfficeType(null);
//       form.resetFields();
//       form.setFieldsValue({ selectedOffice: "addNew" });
//     } else {
//       setIsAddingNewOffice(false);

//       if (Array.isArray(initValues)) {
//         const selectedSetting = initValues.find(
//           (setting) => setting.company_name === selectedOffice
//         );

//         console.log("Selected setting:", selectedSetting); // Debug log

//         if (selectedSetting) {
//           console.log("Setting office type ID:", selectedSetting.officeTypeId); // Debug log
//           setSelectedOfficeType(selectedSetting.officeTypeId);

//           // Prepare form values
//           const formValues = {
//             ...selectedSetting,
//             selectedOffice,
//             officeTypeId: selectedSetting.officeTypeId,
//             phone: selectedSetting.phone?.replace("+91", ""),
//           };

//           console.log("Setting form values:", formValues); // Debug log
//           form.setFieldsValue(formValues);
//         }
//       }
//     }
//   };

//   return (
//     <Fragment>
//       <UserPrivateComponent permission={"create-setting"}>
//         <Row className="mr-top" justify="center">
//           <Col
//             xs={24}
//             sm={24}
//             md={24}
//             lg={11}
//             xl={11}
//             className="border rounded column-design"
//           >
//             <Card bordered={false}>
//               <Title level={4} className="m-2 mb-4 text-center">
//                 Company Setting
//               </Title>
//               {loader ? (
//                 <Loader />
//               ) : initValues ? (
//                 <Form
//                   initialValues={{
//                     ...initValues,
//                   }}
//                   form={form}
//                   name="basic"
//                   labelCol={{
//                     span: 7,
//                   }}
//                   labelWrap
//                   wrapperCol={{
//                     span: 16,
//                   }}
//                   // onFinish={onFinish}
//                   // onFinishFailed={onFinishFailed}
//                   autoComplete="off"
//                 >
//                   {/* Dropdown for selecting the office name */}
//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Select Office"
//                     name="selectedOffice"
//                   >
//                     <Select
//                       onChange={(selectedOffice) =>
//                         handleOfficeChange(selectedOffice)
//                       }
//                     >
//                       <Option value="addNew">Add New Office</Option>
//                       {officeNames.map((officeName) => (
//                         <Option key={officeName} value={officeName}>
//                           {officeName}
//                         </Option>
//                       ))}
//                     </Select>
//                   </Form.Item>

//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     fields={[{ name: "Company Name" }]}
//                     label="Company Name"
//                     name="company_name"
//                     rules={[
//                       {
//                         required: true,
//                         message: "Please input Company name!",
//                       },
//                     ]}
//                   >
//                     <Input />
//                   </Form.Item>
//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Office Type"
//                     name="officeTypeId"
//                     rules={[
//                       {
//                         required: true,
//                         message: "Please select Office Type!",
//                       },
//                     ]}
//                   >
//                     <Row justify="space-between" align="middle">
//                       <Col span={20}>
//                         {/* <Select
//                             placeholder="Select Office Type"
//                             onChange={(value) => {
//                               // Explicitly set the officeTypeId in the form
//                               form.setFieldsValue({ officeTypeId: value });
//                             }}
//                           >
//                             {companyTypes.map((type) => (
//                               <Option key={type.id} value={type.id}>
//                                 {type.officeType}
//                               </Option>
//                             ))}
//                           </Select> */}
//                         <Select
//                           placeholder="Select Office Type"
//                           value={selectedOfficeType}
//                           onChange={(value) => {
//                             setSelectedOfficeType(value);
//                             form.setFieldsValue({ officeTypeId: value });
//                           }}
//                         >
//                           {companyTypes.map((type) => (
//                             <Option key={type.id} value={type.id}>
//                               {type.officeType}
//                             </Option>
//                           ))}
//                         </Select>
//                       </Col>
//                       <Col span={4} style={{ textAlign: "right" }}>
//                         <Button type="primary" onClick={showModal}>
//                           +
//                         </Button>
//                       </Col>
//                     </Row>
//                   </Form.Item>

//                   {/* <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     fields={[{ name: "Tagline" }]}
//                     label="Tagline"
//                     name="tag_line"
//                     // rules={[
//                     //   {
//                     //     required: true,
//                     //     message: "Please input Tagline!",
//                     //   },
//                     // ]}
//                   >
//                     <Input />
//                   </Form.Item> */}

//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Address"
//                     name="address"
//                     rules={[
//                       {
//                         required: true,
//                         message: "Please input Address!",
//                       },
//                     ]}
//                   >
//                     <Input />
//                   </Form.Item>

//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Phone Number"
//                     name="phone"
//                     // rules={[
//                     //   {
//                     //     required: true,
//                     //     message: "Please input Phone Number!",
//                     //   },
//                     //   {
//                     //     pattern: /^[0-9]{10}$/,
//                     //     message: "Please enter a valid 10-digit phone number!",
//                     //   },
//                     // ]}
//                   >
//                     <Input
//                       placeholder="Phone"
//                       maxLength={10}
//                       addonBefore="+91"
//                     />
//                   </Form.Item>

//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Email Address"
//                     name="email"
//                     rules={[
//                       // {
//                       //   required: true,
//                       //   message: "Please input Email Address!",
//                       // },
//                       { type: "email", message: "Please enter a valid email!" },
//                     ]}
//                   >
//                     <Input />
//                   </Form.Item>

//                   <Form.Item
//                     style={{ marginBottom: "10px" }}
//                     label="Website"
//                     name="website"
//                     rules={[
//                       // {
//                       //   required: true,
//                       //   message: "Please input Website!",
//                       // },
//                       { type: "url", message: "Please enter a valid URL!" },
//                     ]}
//                   >
//                     <Input />
//                   </Form.Item>

//                   {/* <Col xs={24} sm={12} md={12}> */}
//                   <Form.Item label="Company Logo">
//                     <Upload
//                       listType="picture-card"
//                       beforeUpload={() => false}
//                       fileList={thumbFileList}
//                       maxCount={1}
//                       onChange={handleThumbImageChange}
//                       accept="image/jpeg,image/png"
//                     >
//                       {thumbFileList.length >= 1 ? null : (
//                         <div>
//                           <PlusOutlined />
//                           <div style={{ marginTop: 8 }}>Thumbnail</div>
//                         </div>
//                       )}
//                     </Upload>
//                   </Form.Item>
//                   {/* </Col> */}

//                   {/* <Form.Item
//                       style={{ marginBottom: "10px" }}
//                       label="Footer"
//                       name="footer"
//                       rules={[
//                         {
//                           required: true,
//                           message: "Please input Footer!",
//                         },
//                       ]}
//                     >
//                       <Input />
//                     </Form.Item> */}

//                   <Form.Item className={styles.addBtnContainer}>
//                     {isAddingNewOffice ? (
//                       <Button
//                         type="primary"
//                         shape="round"
//                         size="large"
//                         loading={loader}
//                         onClick={() =>
//                           onClickLoading(form.getFieldsValue(), true)
//                         }
//                       >
//                         Add New Office
//                       </Button>
//                     ) : (
//                       <Button
//                         type="primary"
//                         shape="round"
//                         size="large"
//                         loading={loader}
//                         onClick={() =>
//                           onClickLoading(
//                             form.getFieldsValue(),
//                             false,
//                             initValues
//                           )
//                         }
//                       >
//                         Update Office Details
//                       </Button>
//                     )}
//                   </Form.Item>
//                 </Form>
//               ) : (
//                 <Loader />
//               )}
//             </Card>
//           </Col>
//         </Row>
//         {/* Modal for adding a new office type */}
//         <Modal
//           title="Add Office Type"
//           visible={isModalVisible}
//           onOk={handleOk}
//           onCancel={handleCancel}
//         >
//           <Form form={modalForm} layout="vertical">
//             <Form.Item
//               label="Office Type"
//               name="officeType"
//               rules={[{ required: true, message: "Please input office type!" }]}
//             >
//               <Input />
//             </Form.Item>
//           </Form>
//         </Modal>
//       </UserPrivateComponent>
//     </Fragment>
//   );
// };

// export default AddDetails;

